import React, { useMemo } from "react";
import { selectionsSliceActions } from "../store/slices/selectionsSlice";
import { useDispatch, useSelector } from 'react-redux';
import classes from './TopBar.module.css';
import { getCauseCategories, normalizeSelectedCause } from "../util/causes";

const TopBar = () => {
    const dispatch = useDispatch();
    const displacementReasons = useSelector(state => state.agg.displacement_reasons);
    const mapLoading = useSelector(state => state.selection.mapLoading);
    // Read the cause from Redux so the tabs reflect selections made elsewhere
    // (e.g. the AI agent's "Visualize this data"), and so clicking the
    // already-active cause is a no-op instead of stranding the map loader.
    const selectedCause = useSelector(state => state.selection.cause);
    // Drill-down context used to grey out reasons that have no data for the
    // selected country + year. Only applies at country/admin level — region and
    // global views keep every reason enabled.
    const selectedCountry = useSelector(state => state.selection.country);
    const selectedCountryPcode = useSelector(state => state.selection.countrypcode);
    const selectedYear = useSelector(state => state.selection.year);
    // Cause-independent source: the global country aggregate is loaded once and
    // never filtered by the selected cause, so it reliably tells us which
    // reasons have data — consistently whether "All" or a specific cause is on.
    const allCountriesList = useSelector(state => state.agg.country_Agg);
    const categories = [
        "All",
        ...(Array.isArray(displacementReasons) ? displacementReasons : [])
            .map((item) =>
                typeof item === "string" ? item : item?.reason_for_displacement
            )
            .filter(
                (item) =>
                    item &&
                    (typeof item !== "string" || item.trim().toLowerCase() !== "all")
            ),
    ];

    // Set of normalised reason categories that actually carry IDP figures for
    // the selected year at the current drill level. `null` means "no gating"
    // (region/global view, or data not loaded yet) — every reason stays
    // enabled in that case.
    const reasonsWithData = useMemo(() => {
        // Only gate once a country is selected; region/global views keep every
        // reason enabled.
        if (!selectedCountry) return null;
        const source = Array.isArray(allCountriesList) ? allCountriesList : [];
        if (!source.length) return null;
        const selectedYearNumber = Number(selectedYear);
        const hasSelectedYear = Number.isFinite(selectedYearNumber);
        const normalizedCountry = (selectedCountry || "").trim().toLowerCase();
        const available = new Set();
        source.forEach((row) => {
            const rowName = (row.adm0_0_name || row.admin_0_name || "")
                .trim()
                .toLowerCase();
            const isCountry =
                rowName === normalizedCountry ||
                (selectedCountryPcode && row.admin_0_pcode === selectedCountryPcode);
            if (!isCountry) return;
            if (hasSelectedYear && Number(row.year) !== selectedYearNumber) return;
            const idps = Number(row.idps);
            if (!Number.isFinite(idps) || idps <= 0) return;
            getCauseCategories(row.reason_for_displacement).forEach((cause) =>
                available.add(cause)
            );
        });
        // If we couldn't determine any reasons, don't gate (avoid trapping).
        return available.size ? available : null;
    }, [allCountriesList, selectedCountry, selectedCountryPcode, selectedYear]);

    const isReasonDisabled = (item) => {
        // "All" and the currently selected cause are always selectable; only
        // gate when we have a dataset to judge against.
        if (item === "All" || item === selectedCause || !reasonsWithData) return false;
        return !reasonsWithData.has(normalizeSelectedCause(item));
    };

    const selectCause = (e) => {
        if (mapLoading || e.target.value === selectedCause) return;
        dispatch(selectionsSliceActions.setMapLoading(true));
        dispatch(selectionsSliceActions.setRoundFilter(null));
        dispatch(selectionsSliceActions.setCause(e.target.value));
    }

    return (
        <>
            <div className={classes.box}>
                {/*<p className={classes.box_title}>REASONS OF DISPLACEMENT</p>*/}
                <div
                    className={`${classes.cause_group}${mapLoading ? ` ${classes.cause_group_disabled}` : ""}`}
                >
                    {categories.map((item, i) => {
                        const isSelected = selectedCause === item;
                        const noData = isReasonDisabled(item);
                        return (
                            <div
                                key={item}
                                className={`${classes.cause_list}${isSelected ? ` ${classes.cause_list_selected}` : ""}${noData ? ` ${classes.cause_list_no_data}` : ""}`}
                                title={noData ? `No data for ${selectedYear}` : undefined}
                            >
                                <input
                                    type="radio"
                                    id={`cause-${i}`}
                                    name="displacement_cause"
                                    value={item}
                                    onChange={selectCause}
                                    disabled={mapLoading || noData}
                                    className={classes.cause_input}
                                />
                                <label
                                    htmlFor={`cause-${i}`}
                                    className={`${classes.cause_list_label}${isSelected ? ` ${classes.cause_list_label_selected}` : ""}${noData ? ` ${classes.cause_list_label_no_data}` : ""}`}
                                >
                                    {item}
                                </label>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    )
}

export default TopBar
